/**
 * 作り直しの ID 型 — 仕様グラフが振る ID は一律 number（NE §1 ID）。
 *
 * 今の契約はノードの ID をパス（名前）の文字列で持ち、要求の層は UUID で持つ。作り直しでは
 * **全型で 1 本の連番**にする。パスを ID にすると改名のたびに全参照を書き換えることになり、
 * パス形は型を一意に決めないので読取に型の指定が要る。切替で全件を書き直す今が、型を替える
 * 費用の一番小さい 1 回である。
 *
 * 番号は 3 種類に分かれる。**どれも正の整数で、0 と負と小数と文字列は受け付けない。**
 *
 * | 種類 | 何を指すか | 採番 |
 * |---|---|---|
 * | 要素の id（`specElementId`） | ノード 1 件。型をまたいで一意 | Firestore のカウンタ |
 * | 集約の中の局所 id（`localElementId`） | 判定表の行・業務フローの節点・並べ替えのキーの順位 | その集約の中で 1 から |
 * | 案件番号（`specPjNumberValue`） | 案件 1 件（139162） | Backlog の課題番号の数値部分 |
 *
 * **負の数はこのモジュールでは受け付けない。** 同じ呼び出しの中で作る要素を指す仮の番号は
 * 負の数だが、それは封筒（書込の操作の並び）の関心であって要素そのものの欄ではない。要素の
 * 欄に負の数が入れる形にすると、仮の番号が解決されないまま保存できてしまう。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しの要素が使う ID（要素 id / 集約内の局所 id / 案件番号）の zod 型の単一ソース
 * @graph-connects none
 */
import { z } from 'zod';
/**
 * 要素 1 件を指す id。全型で 1 本の連番で、1 から始まる。
 *
 * 不変である。改名はラベル（名前の作り）の更新になるので、id は動かない。
 *
 * @returns 正の整数を要求する zod 型
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 要素 1 件を指す番号（全型で 1 本の連番）の zod 型
 * @graph-connects none
 */
export function specElementId() {
    return z.number().int().positive();
}
/**
 * 新規のときだけ省ける id。**省略 = 新規**で、サーバーが採番して応答で返す。
 *
 * `null` は受け付けない。「まだ無い」を `undefined` と `null` の 2 つで表せると、書込側が
 * どちらで送るかを選べてしまい、受け側がその両方を畳む分岐を持つことになる。
 *
 * @returns 省略可能な要素 id の zod 型
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 新規（省略）と更新（値あり）を 1 つの欄で表す要素 id の zod 型
 * @graph-connects none
 */
export function optionalSpecElementId() {
    return specElementId().optional();
}
/**
 * 集約の中だけで通じる局所 id。その集約の中で 1 から振り、並べ替えても変わらない。
 *
 * 判定表の行がこれを持つのは、「上から先に当たった行」の判定表では入力の値が同じ行が複数
 * 在りうるためである。入力の値を鍵にすると、案件の差分で行が黙って 1 つに潰れる（決定。
 * 2026-09-21）。業務フローの節点も同じ理由で番号を持つ。業務の結末の行は番号を持たず、
 * 結果の値（用語の id）で指す — 行は結果の取りうる値ぶん並び、結果の値がそのまま行の鍵になる
 * ためである（API §3.4）。
 *
 * グローバルな連番は使わない。集約は丸ごと置き換わるので、要素 id と同じ連番から取ると
 * 書き直すたびに番号が飛ぶ。
 *
 * @returns 正の整数を要求する zod 型
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 集約の中で 1 から振る局所 id（判定表の行・業務フローの節点・並べ替えのキーの順位）の zod 型
 * @graph-connects none
 */
export function localElementId() {
    return z.number().int().positive();
}
/**
 * 案件番号。`AIRCLOSET-139162` の数値部分（139162）がそのまま入る（決定。2026-09-21）。
 *
 * 今は文字列の `pjNumber` と分割用の `pjSeq` の 2 列に割れている。番号 1 本にすると、
 * 2 つが食い違う行を作れなくなる。関門は「1 以上の整数」だけで、桁数は縛らない — 桁で縛って
 * いたのは文字列の形（`AIRCLOSET-<5 桁以上>`）が打ち間違いを通していたためで、整数で受ける
 * なら形の取り違えはそもそも起きない。画面は `AIRCLOSET-` を前に付けて出す。
 *
 * @returns 1 以上の整数を要求する zod 型
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件番号（number）の zod 型。文字列の PJ 番号と分割キーの 2 列を 1 つに畳む
 * @graph-connects none
 */
export function specPjNumberValue() {
    return z.number().int().positive();
}
/**
 * 局所 id の並びが 1 から始まる欠番なしの連番かを判定する。
 *
 * 判定表の行・業務フローの節点・並べ替えのキーの順位は「その集約の中で 1 から振る」と決めてある
 * （NE §1 ID）。欠番や重複を通すと、案件の差分で行を対応づける鍵が壊れる — 同じ番号の行が
 * 2 つ在れば、どちらが前の版かを機械では決められない。
 *
 * @param ids 集約の中の局所 id の並び（宣言された順）
 * @returns 1 から欠番なしで並んでいれば true
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 集約の中の局所 id が 1 起点の欠番なし連番であることを判定する
 * @graph-connects none
 */
export function isSequentialLocalIds(ids) {
    const sorted = [...ids].sort((left, right) => left - right);
    return sorted.every((id, index) => id === index + 1);
}
//# sourceMappingURL=ids.js.map